import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Users, FileText, Briefcase, DollarSign, 
  Zap, Package, Eye, MousePointer, ArrowUpRight, ArrowDownRight,
  Calendar, RefreshCw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const StatCard = ({ icon: Icon, label, value, subValue, trend, color = 'emerald' }) => {
  const colorClasses = {
    emerald: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30 text-emerald-400',
    cyan: 'from-cyan-500/20 to-cyan-600/10 border-cyan-500/30 text-cyan-400',
    purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30 text-purple-400',
    amber: 'from-amber-500/20 to-amber-600/10 border-amber-500/30 text-amber-400',
    blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30 text-blue-400',
    rose: 'from-rose-500/20 to-rose-600/10 border-rose-500/30 text-rose-400',
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} border rounded-xl p-5 relative overflow-hidden`}>
      <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-zinc-400 text-sm mb-1">{label}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
          {subValue && <p className="text-xs text-zinc-500 mt-1">{subValue}</p>}
        </div>
        <div className={`p-2 rounded-lg bg-white/10 ${colorClasses[color].split(' ').pop()}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-3 text-sm ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
          {trend >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
          <span>{Math.abs(trend)}% vs mes anterior</span>
        </div>
      )}
    </div>
  );
};

const DashboardPage = () => {
  const { fetchWithAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    products: { total_products: 0, low_stock_count: 0 },
    quotes: { total_quotes: 0, new_this_month: 0, conversion_rate: 0 },
    projects: { total_projects: 0, in_progress: 0, total_capacity_kw: 0 },
    clients: { total_clients: 0, active_clients: 0 },
    analytics: { visits_today: 0, clicks_today: 0, visits_this_month: 0 }
  });

  const loadStats = async () => {
    setLoading(true);
    try {
      const [productsRes, quotesRes, projectsRes, clientsRes, analyticsRes] = await Promise.all([
        fetchWithAuth('/api/products/stats'),
        fetchWithAuth('/api/quotes/stats'),
        fetchWithAuth('/api/projects/stats'),
        fetchWithAuth('/api/clients/stats'),
        fetchWithAuth('/api/analytics/summary')
      ]);

      const [products, quotes, projects, clients, analytics] = await Promise.all([
        productsRes.json(),
        quotesRes.json(),
        projectsRes.json(),
        clientsRes.json(),
        analyticsRes.json()
      ]);

      setStats({ products, quotes, projects, clients, analytics });
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadStats();
  }, []);

  const formatCurrency = (value) => {
    if (value >= 1000000000) return `${(value / 1000000000).toFixed(1)}B Gs`;
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M Gs`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K Gs`;
    return `${value} Gs`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
          <p className="text-zinc-400">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-zinc-400 text-sm">Resumen general de Zenex</p>
        </div>
        <button
          onClick={loadStats}
          className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <StatCard
          icon={FileText}
          label="Presupuestos Totales"
          value={stats.quotes.total_quotes}
          subValue={`${stats.quotes.new_this_month} este mes`}
          color="emerald"
        />
        <StatCard
          icon={TrendingUp}
          label="Tasa de Conversión"
          value={`${stats.quotes.conversion_rate}%`}
          color="cyan"
        />
        <StatCard
          icon={Briefcase}
          label="Proyectos Activos"
          value={stats.projects.in_progress}
          subValue={`${stats.projects.total_projects} totales`}
          color="purple"
        />
        <StatCard
          icon={Zap}
          label="Capacidad Instalada"
          value={`${stats.projects.total_capacity_kw} kW`}
          color="amber"
        />
        <StatCard
          icon={Users}
          label="Clientes"
          value={stats.clients.total_clients}
          subValue={`${stats.clients.active_clients} activos`}
          color="blue"
        />
        <StatCard
          icon={Package}
          label="Productos"
          value={stats.products.total_products}
          subValue={stats.products.low_stock_count > 0 ? `⚠️ ${stats.products.low_stock_count} stock bajo` : 'Stock OK'}
          color={stats.products.low_stock_count > 0 ? 'rose' : 'emerald'}
        />
        <StatCard
          icon={Eye}
          label="Visitas Hoy"
          value={stats.analytics.visits_today}
          subValue={`${stats.analytics.visits_this_month} este mes`}
          color="cyan"
        />
        <StatCard
          icon={MousePointer}
          label="Clicks Hoy"
          value={stats.analytics.clicks_today}
          color="purple"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Presupuestos por estado */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-400" />
            Presupuestos por Estado
          </h3>
          <div className="space-y-3">
            {Object.entries(stats.quotes.by_status || {}).map(([status, count]) => {
              const statusLabels = {
                nuevo: 'Nuevos', contactado: 'Contactados', en_negociacion: 'En Negociación',
                aprobado: 'Aprobados', rechazado: 'Rechazados', convertido: 'Convertidos', expirado: 'Expirados'
              };
              const statusColors = {
                nuevo: 'bg-blue-500', contactado: 'bg-yellow-500', en_negociacion: 'bg-purple-500',
                aprobado: 'bg-emerald-500', rechazado: 'bg-red-500', convertido: 'bg-cyan-500', expirado: 'bg-zinc-500'
              };
              const total = stats.quotes.total_quotes || 1;
              const percentage = ((count / total) * 100).toFixed(0);
              
              return (
                <div key={status} className="flex items-center gap-3">
                  <span className="text-sm text-zinc-400 w-28">{statusLabels[status] || status}</span>
                  <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${statusColors[status] || 'bg-zinc-500'} transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-white w-12 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Proyectos por estado */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-purple-400" />
            Proyectos por Estado
          </h3>
          <div className="space-y-3">
            {Object.entries(stats.projects.by_status || {}).map(([status, count]) => {
              const statusLabels = {
                lead: 'Lead', cotizado: 'Cotizado', aprobado: 'Aprobado',
                en_planificacion: 'En Planificación', compra_materiales: 'Compra Materiales',
                en_instalacion: 'En Instalación', en_revision: 'En Revisión',
                completado: 'Completado', entregado: 'Entregado', en_garantia: 'En Garantía', cancelado: 'Cancelado'
              };
              const statusColors = {
                lead: 'bg-zinc-500', cotizado: 'bg-blue-500', aprobado: 'bg-emerald-500',
                en_planificacion: 'bg-yellow-500', compra_materiales: 'bg-orange-500',
                en_instalacion: 'bg-purple-500', en_revision: 'bg-pink-500',
                completado: 'bg-cyan-500', entregado: 'bg-teal-500', en_garantia: 'bg-indigo-500', cancelado: 'bg-red-500'
              };
              const total = stats.projects.total_projects || 1;
              const percentage = ((count / total) * 100).toFixed(0);
              
              return (
                <div key={status} className="flex items-center gap-3">
                  <span className="text-sm text-zinc-400 w-32 truncate">{statusLabels[status] || status}</span>
                  <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${statusColors[status] || 'bg-zinc-500'} transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-white w-12 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Accesos Rápidos */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Accesos Rápidos</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <a href="/admin/products" className="p-4 bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700 rounded-lg text-center transition-colors group">
            <Package className="w-8 h-8 mx-auto mb-2 text-emerald-400 group-hover:scale-110 transition-transform" />
            <span className="text-sm text-zinc-300">Nuevo Producto</span>
          </a>
          <a href="/admin/projects" className="p-4 bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700 rounded-lg text-center transition-colors group">
            <Briefcase className="w-8 h-8 mx-auto mb-2 text-purple-400 group-hover:scale-110 transition-transform" />
            <span className="text-sm text-zinc-300">Nuevo Proyecto</span>
          </a>
          <a href="/admin/clients" className="p-4 bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700 rounded-lg text-center transition-colors group">
            <Users className="w-8 h-8 mx-auto mb-2 text-cyan-400 group-hover:scale-110 transition-transform" />
            <span className="text-sm text-zinc-300">Nuevo Cliente</span>
          </a>
          <a href="/admin/settings" className="p-4 bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700 rounded-lg text-center transition-colors group">
            <DollarSign className="w-8 h-8 mx-auto mb-2 text-amber-400 group-hover:scale-110 transition-transform" />
            <span className="text-sm text-zinc-300">Actualizar Dólar</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
